import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { buildConsultationHref, openEstimateWizard } from "@/lib/cta";
import { trackClick } from "@/lib/analytics";

/**
 * CommercialCTA — the single reusable CTA cluster for service pages,
 * case studies and the About trust band.
 *
 * Every button opens the SAME central conversion journeys:
 *   Get Project Estimate           → ProjectEstimateWizard (with context)
 *   Book a 15-Minute Consultation  → /contact?subject=15-min-consultation (+ context)
 *   Discuss Your Project           → /contact?subject=discuss-your-project (+ context)
 *
 * Analytics: reuses existing GTM/GA4 events. Adds contextual parameters
 * (source_type, service_slug, case_study_slug, market_slug, cta_location)
 * without creating a new event taxonomy.
 *
 * Props (all optional except `variant`):
 *   variant       "estimate-primary" | "estimate-plus-consultation" | "discuss-project"
 *   serviceSlug / serviceName
 *   caseStudySlug / caseStudyName
 *   marketSlug
 *   sourceType    e.g. "service_page", "case_study", "about_trust_band"
 *   ctaLocation   e.g. "hero", "final", "trust_band"
 *   tone          "on-dark" | "on-light" (styling only)
 *   testid        base data-testid prefix
 */
export default function CommercialCTA({
  variant = "estimate-plus-consultation",
  serviceSlug = null,
  serviceName = null,
  caseStudySlug = null,
  caseStudyName = null,
  marketSlug = null,
  sourceType = "generic",
  ctaLocation = "final",
  tone = "on-light",
  testid = "commercial-cta",
}) {
  const commonCtx = {
    serviceSlug,
    serviceName,
    caseStudySlug,
    caseStudyName,
    marketSlug,
    sourceType,
    ctaLocation,
  };

  const onEstimate = () => {
    trackClick("cta_clicked", {
      cta_type: "get_project_estimate",
      source_type: sourceType,
      cta_location: ctaLocation,
      service_slug: serviceSlug || undefined,
      case_study_slug: caseStudySlug || undefined,
      market_slug: marketSlug || undefined,
    });
    openEstimateWizard(commonCtx);
  };

  const onConsultation = () => {
    trackClick("cta_clicked", {
      cta_type: "book_consultation",
      source_type: sourceType,
      cta_location: ctaLocation,
      service_slug: serviceSlug || undefined,
      case_study_slug: caseStudySlug || undefined,
      market_slug: marketSlug || undefined,
    });
  };

  const consultationHref = buildConsultationHref(commonCtx);
  const discussHref = buildConsultationHref({ ...commonCtx });

  const primaryClasses =
    "group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)]";
  const secondaryClasses =
    tone === "on-dark"
      ? "rounded-full px-5 py-3 border border-background/25 text-sm inline-flex items-center gap-2 hover:bg-background/5 text-background"
      : "rounded-full px-5 py-3 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5";

  if (variant === "discuss-project") {
    return (
      <Link
        to={discussHref}
        onClick={() => {
          trackClick("cta_clicked", {
            cta_type: "discuss_project",
            source_type: sourceType,
            cta_location: ctaLocation,
            service_slug: serviceSlug || undefined,
            case_study_slug: caseStudySlug || undefined,
            market_slug: marketSlug || undefined,
          });
        }}
        data-testid={`${testid}-discuss`}
        className={secondaryClasses}
      >
        Discuss Your Project <ArrowRight className="w-4 h-4" />
      </Link>
    );
  }

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <button
        onClick={onEstimate}
        data-testid={`${testid}-estimate`}
        className={primaryClasses}
      >
        <span>Get Project Estimate</span>
        <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
          <ArrowRight className="w-4 h-4" />
        </span>
      </button>
      {variant === "estimate-plus-consultation" && (
        <Link
          to={consultationHref}
          onClick={onConsultation}
          data-testid={`${testid}-consultation`}
          className={secondaryClasses}
        >
          <Calendar className="w-4 h-4" />
          Book a 15-Minute Consultation
        </Link>
      )}
    </div>
  );
}
